import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { productsKeys } from "@/application/query-keys/products.keys";
import {
  productsRepository,
  type UpsertProductInput,
} from "@/infrastructure/repositories/products.supabase.repository";
import type { ProductRow } from "@/infrastructure/supabase/database.types";

export function useProducts() {
  return useQuery({
    queryKey: productsKeys.list(),
    queryFn: () => productsRepository.list(),
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: productsKeys.detail(slug ?? ""),
    queryFn: () => (slug ? productsRepository.getBySlug(slug) : Promise.resolve(null)),
    enabled: typeof slug === "string" && slug.length > 0,
  });
}

export function useUpsertProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertProductInput) => productsRepository.upsert(input),
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: productsKeys.all });
      void qc.invalidateQueries({ queryKey: productsKeys.detail(input.slug) });
    },
  });
}

export function useRemoveProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => productsRepository.remove(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: productsKeys.all });
      const previous = qc.getQueryData<ProductRow[]>(productsKeys.list());
      qc.setQueryData<ProductRow[]>(productsKeys.list(), (current) =>
        (current ?? []).filter((p) => p.id !== id)
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(productsKeys.list(), ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: productsKeys.all });
    },
  });
}
